import React from "react";
import styled, { css } from "styled-components";
import type { ChangeRate } from "types/BusinessDashboard";
import {
  TriangleBlueIcon,
  TriangleRedIcon,
} from "assets/icons/businessDashboard";

const IndicatorSpan = styled.span<{
  $smallFont?: boolean;
  $color?: string;
}>`
  display: flex;
  align-items: center;
  gap: 4px;

  ${({ $smallFont }) =>
    $smallFont
      ? css`
          font-size: 12px;
          font-weight: 500;
          line-height: 16px;
          letter-spacing: -0.2px;
        `
      : css`
          font-size: 15px;
          font-weight: 700;
          line-height: 22px;
          letter-spacing: -0.2px;
        `}

  color: ${({ $color }) => $color || "#6b7280"};
`;

export const SalesChangeIndicator = ({
  changeRate,
  smallFont = false,
  showIcon = true,
  neutral = false,
}: {
  showIcon?: boolean;
  smallFont?: boolean;
  /**
   * 증감의 좋고 나쁨을 색·화살표로 말하지 않는다.
   * 환불·취소처럼 증감 방향의 의미가 다른 분류와 반대인 줄에 쓴다
   * (환불이 늘면 `+`로 나오는데 다른 분류와 같은 규칙을 쓰면
   * "환불이 늘었다"가 좋은 신호로 보인다).
   */
  neutral?: boolean;
  changeRate?: ChangeRate;
}) => {
  if (!changeRate) return null; // 데이터가 없는 경우

  if (changeRate.calculable === null) {
    return (
      <IndicatorSpan $smallFont={smallFont} $color="#6b7280">
        0.0%
      </IndicatorSpan>
    );
  }

  // 계산 불가능한 경우
  if (!changeRate.calculable) {
    const Icon =
      changeRate.positive === true ? TriangleBlueIcon : TriangleRedIcon;
    const color = neutral
      ? "#6b7280"
      : changeRate.positive === true
        ? "#1F6AFF"
        : "#FD4235";
    const valueText = changeRate.positive === true ? "∞" : "-∞";

    return (
      <IndicatorSpan $smallFont={smallFont} $color={color}>
        {!neutral && showIcon && <Icon />}
        {valueText}%
      </IndicatorSpan>
    );
  }

  // 계산 가능한 경우
  const value = changeRate.value ?? 0;

  let IconComponent;
  let color;

  if (neutral) {
    color = "#6b7280";
  } else if (value > 0) {
    IconComponent = TriangleBlueIcon;
    color = "#1F6AFF";
  } else if (value < 0) {
    IconComponent = TriangleRedIcon;
    color = "#FD4235";
  } else {
    color = "#6b7280";
  }

  return (
    <IndicatorSpan $smallFont={smallFont} $color={color}>
      {value !== 0 && showIcon && IconComponent && <IconComponent />}
      {(!showIcon || neutral) && value > 0
        ? "+" + value.toFixed(1)
        : value.toFixed(1)}
      %
    </IndicatorSpan>
  );
};
